// /notifications — Notification Center (PAGES.md §3.72)
//
// All notifications for the current user. Grouped by date. Mark as read,
// mark all as read. A click opens the linked record, else the notification's
// detail view (/notifications/:id, T24).
// SHARED vertical. No feature flag. Min role: CASHIER (5) — all users.
// Traceability: FR-NOTIF-001 to FR-NOTIF-010

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  Pagination,
  useToast,
} from '@bbpos/ui';
import { notificationService } from '@/services/notification.service';
import type { Notification, NotificationType } from '@/services/notification.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, cn, toneClasses } from '@/constants/design-tokens';
import { connectSocket, onSocketEvent } from '@/lib/socket-client';
import { SwipeableNotificationCard } from './SwipeableNotificationCard';
import { TYPE_META } from './NotificationCardBody';
import { NOTIFICATIONS_QUERY_KEY, notificationHref } from './notification-links';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/constants/product';
import { DEFAULT_TIMEZONE } from '@bbpos/types';
import { useStoreTimezone } from '@/lib/store-clock';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

// ─── Helpers ────────────────────────────────────────────────────────────────

type DateGroup = 'Today' | 'Yesterday' | 'This Week' | 'Older';

// Map a Date to its calendar day on the business's clock as an integer
// day-count (days since epoch). Bucketing by the business timezone — the
// tenant's (T28), IST when unknown — not the viewer's browser timezone keeps
// Today/Yesterday/This Week consistent for every viewer, so a notification
// created late in the business's day stays in the right group even for an
// admin elsewhere or an item near midnight (issue #260).
function businessDayNumber(d: Date, timeZone: string): number {
  // en-CA yields YYYY-MM-DD; interpreting it at UTC midnight makes the diff
  // between two business days a clean integer regardless of the viewer's timezone.
  const ymd = new Intl.DateTimeFormat('en-CA', { timeZone }).format(d);
  return Math.floor(Date.parse(`${ymd}T00:00:00Z`) / 86400000);
}

export function getDateGroup(iso: string, timeZone: string = DEFAULT_TIMEZONE): DateGroup {
  const diff = businessDayNumber(new Date(), timeZone) - businessDayNumber(new Date(iso), timeZone);

  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff <= 6) return 'This Week';
  return 'Older';
}

function groupNotifications(
  notifications: Notification[],
  timeZone: string,
): { group: DateGroup; items: Notification[] }[] {
  const groups: Record<DateGroup, Notification[]> = { Today: [], Yesterday: [], 'This Week': [], Older: [] };
  notifications.forEach((n) => groups[getDateGroup(n.created_at, timeZone)].push(n));
  const order: DateGroup[] = ['Today', 'Yesterday', 'This Week', 'Older'];
  return order.filter((g) => groups[g].length > 0).map((g) => ({ group: g, items: groups[g] }));
}

// ─── Icons ─────────────────────────────────────────────────────────────────

function BellEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function CheckAllIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

// Filter-chip types exposed in the UI. Only user-facing notification types
// (no platform lifecycle types like PLATFORM_SUSPENSION) are selectable.
const FILTER_TYPES: NotificationType[] = [
  'LOW_STOCK',
  'EXPENSE_APPROVAL',
  'APPROVAL_REQUEST',
  'APPROVAL_RESULT',
  'ORDER_NEW',
  'ORDER_UPDATE',
  'SHIFT_REMINDER',
  'TRIAL_EXPIRY',
  'SYSTEM_ANNOUNCEMENT',
  'REPORT_READY',
  'BACKUP_COMPLETE',
  'CREDIT_LIMIT',
  'SYNC_CONFLICT',
  'QUOTE_APPROVED',
  'QUOTE_REJECTED',
  'QUOTE_CHANGES_REQUESTED',
  'VENDOR_BILL_DUE_SOON',
  'VENDOR_BILL_OVERDUE',
  'PO_APPROVAL_REQUEST',
];

export const TYPE_LABELS: Record<NotificationType, string> = {
  LOW_STOCK: 'Low stock',
  EXPENSE_APPROVAL: 'Expense approval',
  APPROVAL_REQUEST: 'Approval request',
  APPROVAL_RESULT: 'Approval result',
  ORDER_NEW: 'New order',
  ORDER_UPDATE: 'Order update',
  SHIFT_REMINDER: 'Shift reminder',
  TRIAL_EXPIRY: 'Trial expiry',
  SYSTEM_ANNOUNCEMENT: 'Announcement',
  REPORT_READY: 'Report ready',
  BACKUP_COMPLETE: 'Backup',
  CREDIT_LIMIT: 'Credit limit',
  SYNC_CONFLICT: 'Sync conflict',
  TABLE_STATUS: 'Table',
  PLATFORM_SUSPENSION: 'Suspension',
  TENANT_CANCELLED: 'Cancelled',
  BILLING_FAILED: 'Billing failed',
  EMAIL_DELIVERY_FAILED: 'Email delivery failed',
  EMAIL_DELIVERY_RESTORED: 'Email delivery restored',
  QUOTE_APPROVED: 'Quote approved',
  QUOTE_REJECTED: 'Quote declined',
  QUOTE_CHANGES_REQUESTED: 'Quote changes requested',
  VENDOR_BILL_DUE_SOON: 'Vendor bill due soon',
  VENDOR_BILL_OVERDUE: 'Vendor bill overdue',
  PO_APPROVAL_REQUEST: 'PO approval',
};

function NotificationCenterContent() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const roleLevel = useAuthStore((s) => ROLE_LEVELS[s.profile?.role ?? ''] ?? 0);
  const [searchParams, setSearchParams] = useSearchParams();

  const urlPage = Number(searchParams.get('page') ?? '1');
  const urlTypes = searchParams.getAll('type').filter((t): t is NotificationType =>
    FILTER_TYPES.includes(t as NotificationType),
  );
  const urlUnreadOnly = searchParams.get('unread') === '1';

  const [page, setPage] = useState(urlPage);
  const [typeFilters, setTypeFilters] = useState<Set<NotificationType>>(
    () => new Set(urlTypes),
  );
  const [unreadOnly, setUnreadOnly] = useState<boolean>(urlUnreadOnly);

  // Persist all filter + page state back to URL for deep links.
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', String(page));
    if (unreadOnly) params.set('unread', '1');
    typeFilters.forEach((t) => params.append('type', t));
    setSearchParams(params, { replace: true });
  }, [page, typeFilters, unreadOnly, setSearchParams]);

  // Any filter change should reset pagination to the first page.
  const toggleTypeFilter = useCallback((t: NotificationType) => {
    setTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setTypeFilters(new Set());
    setUnreadOnly(false);
    setPage(1);
  }, []);

  // Data
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const hasActiveFilters = typeFilters.size > 0 || unreadOnly;

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      // The backend OR-filters on a comma-separated `type` list, so every
      // selected chip is matched server-side across the full result set —
      // pagination and totals stay correct with any number of chips active.
      const typeParam =
        typeFilters.size > 0 ? Array.from(typeFilters).join(',') : undefined;
      const result = await notificationService.list({
        page,
        type: typeParam,
        is_read: unreadOnly ? false : undefined,
      });
      setNotifications(result.data);
      setTotalItems(result.meta.total);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load notifications.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, typeFilters, unreadOnly, toast]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);
  useEffect(() => { fetchUnreadCount(); }, [fetchUnreadCount]);

  // FR-NOTIF-004 — realtime inbox refresh. The app-level
  // `useRealtimeNotifications` hook invalidates a React Query cache key,
  // but this page uses local state, so we subscribe to `notification:new`
  // directly and debounce a refetch (a burst of events shouldn't trigger
  // N parallel requests).
  useEffect(() => {
    connectSocket();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refreshSoon = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void fetchNotifications();
        void fetchUnreadCount();
      }, 250);
    };
    const unsubscribe = onSocketEvent('notification:new', refreshSoon);
    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [fetchNotifications, fetchUnreadCount]);

  // Dedupe mark-read calls across tap + swipe so we never double-decrement
  // the unread counter if a user fires both paths for the same id.
  const markedReadRef = useRef<Set<string>>(new Set());

  const markReadById = useCallback(async (id: string) => {
    if (markedReadRef.current.has(id)) return;
    markedReadRef.current.add(id);
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) => prev.map((item) => (item.id === id ? { ...item, is_read: true } : item)));
      setUnreadCount((c) => Math.max(0, c - 1));
      // Reconcile the optimistic decrement against the authoritative server
      // count so a partial/failed server-side mark (or concurrent activity in
      // another tab) can't leave the pill and nav badge drifted out of sync.
      void fetchUnreadCount();
      // The header bell reads its own query (T24).
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    } catch {
      // Roll back dedupe so the user can retry after a transient failure.
      markedReadRef.current.delete(id);
      toast({ title: 'Failed to mark as read', variant: 'error' });
    }
  }, [toast, fetchUnreadCount, queryClient]);

  // T24: the linked record when the producer set one, else the detail view.
  const handleNotificationClick = useCallback(async (n: Notification) => {
    if (!n.is_read) {
      await markReadById(n.id);
    }
    navigate(notificationHref(n, roleLevel));
  }, [markReadById, navigate, roleLevel]);

  const handleMarkAllRead = useCallback(async () => {
    setIsMarkingAll(true);
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => {
        // Seed the dedupe guard so a late swipe/tap can't double-decrement.
        prev.forEach((n) => markedReadRef.current.add(n.id));
        return prev.map((n) => ({ ...n, is_read: true }));
      });
      setUnreadCount(0);
      // Reconcile with the server: markAllAsRead can partially succeed (some
      // items stay unread server-side) while still resolving, so refetch the
      // authoritative count instead of assuming it's 0.
      void fetchUnreadCount();
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      toast({ title: 'All notifications marked as read', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to mark all as read.', variant: 'error' });
    } finally {
      setIsMarkingAll(false);
    }
  }, [toast, fetchUnreadCount, queryClient]);

  useEffect(() => { document.title = 'Notifications — Orio360 Admin'; }, []);

  // Group notifications by the business's day (T28)
  const timeZone = useStoreTimezone();
  const grouped = useMemo(() => groupNotifications(notifications, timeZone), [notifications, timeZone]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <PageContainer
      title="Notifications"
      subtitle="Stay updated on your business activity"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Notifications' },
      ]}
      actions={
        unreadCount > 0 ? (
          <Button variant="secondary" size="sm" onClick={handleMarkAllRead} disabled={isMarkingAll}>
            <CheckAllIcon /><span className="ml-1.5">{isMarkingAll ? 'Marking...' : 'Mark All Read'}</span>
          </Button>
        ) : undefined
      }
    >
      <div className="animate-fade-in">
        {/* Header Gradient */}
        <div className="bg-gradient-to-r from-sky-600 to-indigo-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(14,165,233,0.45)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Notifications</h1>
              <p className="text-sky-100 mt-1">Stay updated on your business activity</p>
            </div>
            {unreadCount > 0 && (
              <div className={cn('px-3 py-1.5 rounded-full text-sm font-bold bg-white/20 backdrop-blur-sm')}>
                {unreadCount} unread
              </div>
            )}
          </div>
        </div>

        {/* Filter bar */}
        <div className={cn(GLASS_FILTER, 'px-4 py-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 animate-slide-up')}>
          <div
            className="inline-flex items-center gap-0.5 bg-white/60 dark:bg-neutral-800/50 rounded-full p-0.5 shrink-0"
            role="radiogroup"
            aria-label="Read state"
          >
            <button
              type="button"
              role="radio"
              aria-checked={!unreadOnly}
              onClick={() => { setUnreadOnly(false); setPage(1); }}
              className={cn(
                'px-3 py-1 rounded-full text-xs font-semibold transition-colors',
                !unreadOnly
                  ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white shadow-[0_4px_14px_-6px_rgba(79,70,229,0.6)]'
                  : 'text-neutral-600 dark:text-neutral-300 hover:bg-white/70 dark:hover:bg-neutral-800/70',
              )}
            >
              All
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={unreadOnly}
              onClick={() => { setUnreadOnly(true); setPage(1); }}
              className={cn(
                'px-3 py-1 rounded-full text-xs font-semibold transition-colors',
                unreadOnly
                  ? 'bg-gradient-to-r from-violet-600 to-pink-500 text-white shadow-[0_4px_14px_-6px_rgba(219,39,119,0.6)]'
                  : 'text-neutral-600 dark:text-neutral-300 hover:bg-white/70 dark:hover:bg-neutral-800/70',
              )}
            >
              Unread
              {unreadOnly && unreadCount > 0 && (
                <span className="ml-1.5 text-[10px] font-bold tabular-nums">{unreadCount}</span>
              )}
            </button>
          </div>

          <div className="flex flex-wrap gap-1.5 min-w-0 flex-1">
            {FILTER_TYPES.map((t) => {
              const active = typeFilters.has(t);
              const meta = TYPE_META[t];
              const tone = meta ? toneClasses(meta.tone) : toneClasses('brand');
              return (
                <button
                  key={t}
                  type="button"
                  role="checkbox"
                  aria-checked={active}
                  onClick={() => toggleTypeFilter(t)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                    active
                      ? cn(tone.badge, 'ring-1 ring-inset ring-current/30 shadow-sm')
                      : 'bg-white/60 dark:bg-neutral-800/50 text-neutral-600 dark:text-neutral-300 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700/60 hover:bg-white dark:hover:bg-neutral-800',
                  )}
                >
                  {meta && (
                    <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d={meta.iconPath} />
                    </svg>
                  )}
                  {TYPE_LABELS[t]}
                </button>
              );
            })}
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[11px] font-semibold text-brand-600 dark:text-brand-300 hover:underline shrink-0"
            >
              Clear
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className={cn(GLASS, 'p-4 flex gap-3')}>
                <Skeleton variant="circle" width={40} height={40} />
                <div className="flex-1 space-y-2">
                  <Skeleton variant="text" width="40%" height={16} />
                  <Skeleton variant="text" width="70%" height={12} />
                  <Skeleton variant="text" width="20%" height={10} />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<BellEmptyIcon />}
            title={hasActiveFilters ? 'No matches for these filters' : 'No notifications'}
            description={hasActiveFilters
              ? 'Try clearing a filter to broaden the view.'
              : "You're all caught up!"}
          />
        ) : (
          <>
            {grouped.map(({ group, items }) => (
              <div key={group} className="mb-6">
                <h3 className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2 px-1">
                  {group}
                </h3>
                <div className="space-y-2">
                  {items.map((n) => (
                    <SwipeableNotificationCard
                      key={n.id}
                      notification={n}
                      onClick={() => handleNotificationClick(n)}
                      onMarkRead={markReadById}
                    />
                  ))}
                </div>
              </div>
            ))}

            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
}

export function NotificationCenterPage() {
  return (
    <ErrorBoundary>
      <NotificationCenterContent />
    </ErrorBoundary>
  );
}
